import React, { useEffect, useContext, useState } from 'react';
import { ProductContext } from '../ProductProvider';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Helmet } from 'react-helmet-async';
import MainSliderCarosel from './MainSliderCarosel';
import BookCategoryCaroselHome from './BookCategoryCaroselHome';
import AboutUs from './AboutUs';
import ContactUs from './ContactUs';

const homePageStyles = `
  .home-main-wrapper {
    min-height: 100vh;
    background: linear-gradient(135deg, #f8fafc 0%, #eef2f7 50%, #e2e8f0 100%);
    font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    color: #1e293b;
    padding: 20px 0 60px 0;
  }

  .home-content-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 16px;
  }

  /* Announcements & Quick Stats Bar */
  .home-stats-bar {
    background: #ffffff;
    border-radius: 16px;
    border: 1px solid #e2e8f0;
    padding: 14px 24px;
    margin-bottom: 24px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02);
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
  }

  .home-stat-item {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .home-stat-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #e0f2fe;
    color: #0284c7;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.95rem;
    flex-shrink: 0;
  }

  .home-stat-text {
    font-size: 0.85rem;
    font-weight: 700;
    color: #063547;
    line-height: 1.2;
  }

  .home-stat-sub {
    font-size: 0.74rem;
    color: #64748b;
    font-weight: 500;
  }

  .home-section-spacer {
    margin-bottom: 32px;
  }

  /* Floating Scroll-to-Top Button */
  .btn-scroll-top {
    position: fixed;
    bottom: 32px;
    right: 32px;
    z-index: 9999;
    background: linear-gradient(135deg, #063547 0%, #0bb6d7 100%);
    color: #ffffff;
    border: none;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    box-shadow: 0 4px 18px rgba(6, 53, 71, 0.35);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    transition: all 0.25s ease;
  }

  .btn-scroll-top:hover {
    transform: translateY(-4px) scale(1.05);
    box-shadow: 0 8px 24px rgba(11, 182, 215, 0.45);
    color: #ffffff;
  }

  @media (max-width: 768px) {
    .home-stats-bar {
      display: none;
    }
    .home-section-spacer {
      margin-bottom: 20px;
    }
  }
`;

export default function HomePage() {
  const { loggedUser, validateLogin, loginValidated, setLoginValidated } = useContext(ProductContext);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const validateSessionOnLoad = async () => {
      if (loggedUser.isAuthenticated || loginValidated) {
        return;
      }

      setLoginValidated(true);
      try {
        const session = await fetchAuthSession();
        if (session?.tokens?.idToken) {
          await validateLogin();
        }
      } catch (error) {
        // No active session; keep public view
      }
    };

    validateSessionOnLoad();
  }, []);

  useEffect(() => {
    if (loggedUser.isAuthenticated) {
      window.scrollTo(0, 0);
    }
  }, [loggedUser.isAuthenticated]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 250);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="home-main-wrapper">
      <Helmet>
        <title>Vidyajyoti College of Theology Library | Delhi • Institute of Religious Studies</title>
        <meta
          name="description"
          content="Explore over 1,34,000 books, 228 scholarly journals, and theological research collections at Vidyajyoti College of Theology Library, Delhi."
        />
        <meta
          name="keywords"
          content="Theology Library, Vidyajyoti College, Delhi University Theology, VJTR Journal, Religious Studies, Biblical Studies, Christian Theology"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>

      <style>{homePageStyles}</style>

      <div className="home-content-container">
        {/* Quick Highlights & Key Metrics Strip */}
        <div className="home-stats-bar">
          <div className="home-stat-item">
            <div className="home-stat-icon">
              <i className="fa fa-book"></i>
            </div>
            <div>
              <div className="home-stat-text">1,34,000+ Volumes</div>
              <div className="home-stat-sub">Theology & Humanities</div>
            </div>
          </div>

          <div className="home-stat-item">
            <div className="home-stat-icon" style={{ background: '#f3e8ff', color: '#7e22ce' }}>
              <i className="fa fa-newspaper"></i>
            </div>
            <div>
              <div className="home-stat-text">228+ Active Journals</div>
              <div className="home-stat-sub">Domestic & International</div>
            </div>
          </div>

          <div className="home-stat-item">
            <div className="home-stat-icon" style={{ background: '#fef3c7', color: '#b45309' }}>
              <i className="fa fa-th-large"></i>
            </div>
            <div>
              <div className="home-stat-text">65 Disciplines</div>
              <div className="home-stat-sub">2,000+ Classifications</div>
            </div>
          </div>

          <div className="home-stat-item">
            <div className="home-stat-icon" style={{ background: '#ecfdf5', color: '#047857' }}>
              <i className="fa fa-clock"></i>
            </div>
            <div>
              <div className="home-stat-text">Mon–Fri: 8:00 AM – 4:00 PM</div>
              <div className="home-stat-sub">Sat: 8:00 AM – 12:00 Noon</div>
            </div>
          </div>
        </div>

        {/* Section 1: Hero Discovery Slider with Live Search */}
        <section className="home-section-spacer">
          <MainSliderCarosel />
        </section>

        {/* Section 2: Featured Academic Categories Carousel */}
        <section className="home-section-spacer">
          <BookCategoryCaroselHome />
        </section>

        {/* Section 3: About Library Heritage & Archives */}
        <section className="home-section-spacer">
          <AboutUs />
        </section>

        {/* Section 4: Circulation & Reference Help Desk */}
        <section className="home-section-spacer">
          <ContactUs />
        </section>
      </div>

      {/* Floating Scroll To Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="btn-scroll-top"
          aria-label="Scroll to top"
          title="Back to Top"
        >
          <i className="fa fa-arrow-up"></i>
        </button>
      )}
    </div>
  );
}
